import type { CityConfig } from "@/pages/city-template/types";

export const northLasVegasConfig: CityConfig = {
  city: "North Las Vegas",
  state: "Nevada",
  stateAbbr: "NV",
  slug: "north-las-vegas",
  route: "/cities/north-las-vegas",
  title: "Emergency & Expedited Passport Services North Las Vegas NV | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in North Las Vegas, NV — as fast as 24 hours. No North Las Vegas passport agency. Fast Passport Center couriers to the San Francisco Passport Agency at the Phillip Burton Federal Building, about 565 miles away. A+ BBB rated. Serving Aliante, Cheyenne Corridor, Nellis AFB, and all Clark County northern metro travelers heading abroad for military deployment, business, or vacation.",
  h1: "Emergency & Expedited Passport Services in North Las Vegas, NV",
  heroSubtitle: "North Las Vegas is Clark County's northern anchor — a fast-growing city spanning the I-15 corridor from downtown Las Vegas to the foothills of the Sheep Range. Home to Nellis Air Force Base, the Aliante master-planned community, and the Las Vegas Motor Speedway, North Las Vegas serves a diverse population of military families, logistics professionals, and desert suburban residents. Fast Passport Center's registered couriers deliver your documents to the San Francisco Passport Agency at the Phillip Burton Federal Building so you get a new or renewed passport as fast as 24 hours — whether you're an Air Force family facing short-notice PCS orders, a Speedway logistics coordinator heading to an international trade show, or a Cheyenne Corridor professional whose passport expired between trips.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=North%20Las%20Vegas%20Nevada%20skyline%20with%20desert%20mountains%20in%20background%20and%20modern%20suburban%20homes%20in%20Aliante%20community%20during%20golden%20hour%20sunset%2C%20wide%20angle%20desert%20landscape%20with%20residential%20development%20and%20distant%20Strip%20skyline%2C%20clean%20architectural%20photography%20with%20warm%20desert%20tones&width=1800&height=700&seq=north-las-vegas-hero-001&orientation=landscape",
  heroImageAlt: "North Las Vegas Nevada skyline with desert mountains and Aliante community",
  geoPosition: "36.1989;-115.1175",
  geoPlacename: "North Las Vegas, Nevada",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "Phillip Burton Federal Building, 450 Golden Gate Avenue, San Francisco, CA 94102",
    distance: "Approximately 565 miles from North Las Vegas",
  },
  nearbyCitiesZips: [
    { name: "North Las Vegas", zips: "89030, 89031, 89032, 89033, 89036, 89081, 89084, 89085, 89086, 89087" },
    { name: "Aliante", zips: "89084" },
    { name: "Las Vegas", zips: "89101" },
    { name: "Nellis AFB", zips: "89191" },
    { name: "Summerlin", zips: "89135, 89138" },
    { name: "Henderson", zips: "89002, 89011, 89014, 89015, 89016, 89052, 89074, 89077" },
    { name: "Centennial Hills", zips: "89131, 89149" },
    { name: "Mountain's Edge", zips: "89179" },
    { name: "Green Valley", zips: "89014" },
    { name: "Enterprise", zips: "89113, 89118, 89119" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to San Francisco Passport Agency — about 565 miles from North Las Vegas" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Northern Metro Service", desc: "Serving Aliante, Cheyenne Corridor, Nellis AFB, and all North Las Vegas communities" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your North Las Vegas case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Nevada", route: "/states/nevada" },
    { name: "Expedited passport in Las Vegas", route: "/cities/las-vegas" },
    { name: "Urgent passport in North Las Vegas", route: "/cities/north-las-vegas" },
    { name: "Nevada passport hub", route: "/states/nevada" },
    { name: "Fast passport in Henderson NV", route: "/cities/henderson-nv" },
    { name: "Expedited passport in Summerlin", route: "/cities/las-vegas" },
    { name: "Emergency passport in Mesquite NV", route: "/cities/mesquite-nv" },
  ],
  fpcOfficeAddress: "Phillip Burton Federal Building, 450 Golden Gate Avenue, San Francisco, CA 94102",
  fpcOfficeNote: "Fast Passport Center serves North Las Vegas remotely via secure courier. Our registered couriers hand-deliver your documents to the San Francisco Passport Agency at the Phillip Burton Federal Building in downtown San Francisco. North Las Vegas's position in the Las Vegas Valley — just off I-15 — makes overnight courier routing to the Bay Area reliable via McCarran International Airport (LAS). Our dedicated agents ensure North Las Vegas travelers, including military families at Nellis AFB and professionals throughout the I-15 corridor, never need to fly to San Francisco for passport processing.",
  fpcOfficeDistance: "Serving all of North Las Vegas, Aliante, and the northern Clark County metro area",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout North Las Vegas, Aliante, the Cheyenne Corridor, Nellis AFB, Centennial Hills, and all northern Las Vegas Valley communities. From military families deploying overseas to logistics professionals attending international trade shows, we help North Las Vegas travelers get their passports fast.",
  nearbyCitiesIntro: "We serve North Las Vegas and nearby communities including Aliante, Centennial Hills, Summerlin, the Cheyenne Corridor, and the entire northern Clark County metro area.",
  howItWorksIntro: "Most North Las Vegas travelers complete everything online and ship documents overnight. At approximately 565 miles from San Francisco, Fast Passport Center provides fast courier turnaround for northern metro residents — eliminating the need for a round-trip flight to the Bay Area just for passport processing.",
  mistakesIntro: "North Las Vegas travelers, including Nellis AFB military families and I-15 corridor professionals, face unique passport challenges. Here are the most common mistakes we see.",
  testimonialsIntro: "North Las Vegas and Clark County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in North Las Vegas? Here are the most common ones we hear from northern Clark County and the Las Vegas Valley.",
  statePageRoute: "/states/nevada",
  statePageName: "Nevada",
  dmvWebsite: "https://dmv.nv.gov/",
  dmvLabel: "Nevada DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins at Golden Gate Avenue",
        "Life-or-death emergencies only",
        "565 miles to San Francisco",
        "Still requires pre-scheduled NPIC appointment",
        "Round-trip flight typically needed",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No flight or appointment needed",
        "Ideal for Nellis AFB families",
        "Perfect for logistics professionals",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from LAS",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "North Las Vegas is 565 miles from the San Francisco Passport Agency — our courier service saves northern metro residents the round-trip flight to the Bay Area.",
      bullets: ["Clark County coverage", "24–48 hour turnaround available", "No appointment or flight needed"],
      badge: "Fastest in Clark County",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for North Las Vegas adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving North Las Vegas and Aliante"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in North Las Vegas and Clark County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for North Las Vegas residents with DS-64 form guidance and courier routing to San Francisco.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via San Francisco agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Clark County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for North Las Vegas and Clark County residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport North Las Vegas NV", description: "Urgent passport processing for North Las Vegas, NV travelers via San Francisco Passport Agency courier." },
    { name: "Passport Renewal North Las Vegas NV", description: "Fast passport renewal for North Las Vegas and Clark County, Nevada residents." },
    { name: "New Passport North Las Vegas NV", description: "First-time passport application assistance for North Las Vegas and northern metro area residents." },
    { name: "Lost Passport North Las Vegas NV", description: "Passport replacement for lost or stolen passports for North Las Vegas and Clark County residents." },
    { name: "Child Passport North Las Vegas NV", description: "Minor passport services for North Las Vegas and Aliante area families." },
    { name: "Name Change Passport North Las Vegas NV", description: "Passport name change service for North Las Vegas, NV residents." },
  ],
  testimonials: [
    {
      name: "Capt. Marcus T.",
      location: "Nellis AFB, North Las Vegas",
      rating: 5,
      text: "Active duty Air Force with short-notice PCS orders to Ramstein Air Base in Germany. Needed passports for three family members in under two weeks. Fast Passport Center processed all three in 7 business days. My first sergeant recommended them — they handle military families regularly. Saved our entire move timeline.",
      service: "Family Passport Processing",
    },
    {
      name: "Elena R.",
      location: "Aliante, North Las Vegas",
      rating: 5,
      text: "I live in the Aliante master-planned community and work in logistics at the Speedway. Had a last-minute trade expo in Dubai and my passport expired two months prior without me realizing. Fast Passport Center got me a new passport in 5 business days — I made the expo and closed the deal. Their dedicated agent was incredible — daily updates via text.",
      service: "Passport Renewal",
    },
    {
      name: "David and Sofia C.",
      location: "Cheyenne Corridor, North Las Vegas",
      rating: 5,
      text: "Planning a 25th anniversary trip to Italy via LAS. Discovered my passport was still valid but my wife's had expired. She needed a name change too after we updated her documents last year. Fast Passport Center handled both the name change and rush processing — passport arrived in 8 business days. We left for Rome right on schedule.",
      service: "Name Change + Renewal",
    },
    {
      name: "Tech Sgt. Jasmine K.",
      location: "Nellis AFB Housing",
      rating: 5,
      text: "Military spouse whose husband was already deployed to Japan. Needed passports for myself and two kids under 5 to join him. Fast Passport Center processed three child passports plus my renewal — all four documents in 9 business days. The agent was incredibly patient with the DS-11 requirements for minors. Reunited our family overseas.",
      service: "Military Family Processing",
    },
  ],
  facilities: [
    {
      id: "nlv-1",
      name: "North Las Vegas Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1414 E Lake Mead Blvd",
      city: "North Las Vegas",
      state: "NV",
      zip: "89030",
      phone: "(702) 649-2431",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "North Las Vegas — Central",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for North Las Vegas. Appointment required. Located on Lake Mead Boulevard near I-15.",
      icon: "ri-mail-line",
    },
    {
      id: "nlv-2",
      name: "Aliante Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "7051 N Aliante Pkwy",
      city: "North Las Vegas",
      state: "NV",
      zip: "89084",
      phone: "(702) 399-2875",
      hours: "Mon–Fri: 9:00 AM – 3:30 PM",
      distance: "North Las Vegas — Aliante",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Aliante and northern North Las Vegas residents. Newer facility with shorter wait times.",
      icon: "ri-mail-line",
    },
    {
      id: "nlv-3",
      name: "Clark County Government Center",
      type: "County Clerk Passport Acceptance",
      address: "500 S Grand Central Pkwy",
      city: "Las Vegas",
      state: "NV",
      zip: "89155",
      phone: "(702) 455-0000",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "Las Vegas — Downtown, 10 miles south",
      website: "https://www.clarkcountynv.gov",
      websiteLabel: "Clark County Government",
      notes: "Largest passport acceptance facility in Clark County. More appointment slots than local post offices.",
      icon: "ri-government-line",
    },
    {
      id: "nlv-4",
      name: "Centennial Hills Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "5455 N Jensen St",
      city: "Las Vegas",
      state: "NV",
      zip: "89129",
      phone: "(702) 396-1909",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Centennial Hills — 8 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Centennial Hills and northwest valley residents. Good option for those near the Aliante-Centennial corridor.",
      icon: "ri-mail-line",
    },
    {
      id: "nlv-5",
      name: "Las Vegas Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1001 E Sunset Rd",
      city: "Las Vegas",
      state: "NV",
      zip: "89199",
      phone: "(702) 361-9224",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Las Vegas — 12 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest USPS facility in the Las Vegas Valley. More appointment availability and extended service.",
      icon: "ri-mail-line",
    },
    {
      id: "nlv-6",
      name: "Nellis AFB Post Office",
      type: "Military Post Office",
      address: "4370 Washington Ave",
      city: "Nellis AFB",
      state: "NV",
      zip: "89191",
      phone: "(702) 652-4002",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "Nellis AFB — Northeast area",
      website: "https://www.nellis.af.mil",
      websiteLabel: "Nellis AFB",
      notes: "Limited passport acceptance for military personnel and dependents. Must have base access. Call for appointment.",
      icon: "ri-shield-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to SF Agency", agency: "565 miles — round-trip flight to SFO plus downtown SF transit", fpc: "We fly for you — no travel needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-flight-takeoff-line",
      title: "Assuming Las Vegas Has Its Own Passport Agency",
      description: "North Las Vegas residents often assume a passport agency exists nearby given Las Vegas's international airport and tourism infrastructure. There is no passport agency anywhere in Nevada. The closest is the San Francisco Passport Agency — 565 miles away. Any local office claiming to offer 'same-day passports' in Las Vegas is providing courier services, not government agency processing.",
    },
    {
      icon: "ri-plane-line",
      title: "Not Checking Passport Validity for PCS Orders or Deployment",
      description: "Nellis AFB military families are the largest concentration in North Las Vegas. Short-notice Permanent Change of Station (PCS) orders to overseas bases require valid passports for dependents. Many families discover expired or non-existent passports after orders drop — leaving only a few weeks to process. Start passport applications as soon as OCONUS orders are even rumored.",
    },
    {
      icon: "ri-sun-line",
      title: "Desert Heat Damaging Passport Documents Before Mailing",
      description: "North Las Vegas summers routinely reach 110°F+. Passport application documents (especially photos) left in hot mailboxes or vehicles can warp, fade, or become damaged before reaching the processing center. Use air-conditioned drop boxes, mail items early in the day, and never leave sensitive documents in a hot car — courier pickup is recommended.",
    },
    {
      icon: "ri-global-line",
      title: "Passport Book vs. Passport Card Confusion for International Air Travel",
      description: "Many North Las Vegas residents who cross into Mexico by land (via California or Arizona border) have a passport card — which cannot be used for international air travel. If you're flying from LAS to Mexico, Canada, or anywhere international, you need the passport book format. Passport cards are land-and-sea crossing only, not valid for air travel.",
    },
    {
      icon: "ri-building-line",
      title: "Using the Wrong Form for Military Dependents",
      description: "Nellis AFB dependents undergoing PCS moves frequently use the wrong passport form. The DS-11 (first-time application) is required for all children under 16 and adults who've never held a passport, regardless of military status. Some assume their spouse's active-duty status exempts them — it does not. Both parents must consent for minors unless the active-duty spouse provides notarized DS-3053.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Until Speedway Event Season for International Travel Documents",
      description: "North Las Vegas hosts the Las Vegas Motor Speedway, drawing tens of thousands for events like NASCAR and EDC. Many logistics and event professionals realize just before an international trade show or overseas event that their passport has expired or is near expiration. March–May (NASCAR season) and September–November (aviation expos) see peak demand for rush passports in North Las Vegas.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location on W Craig Rd or Losee Rd in North Las Vegas." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the San Francisco Passport Agency — overnight from LAS." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the San Francisco Passport Agency via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your North Las Vegas or Aliante address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is North Las Vegas from the closest passport agency?",
      a: "North Las Vegas is approximately 565 miles from the closest passport agency, the San Francisco Passport Agency at the Phillip Burton Federal Building on Golden Gate Avenue. There is no passport agency anywhere in Nevada. Getting an emergency appointment often requires a round-trip flight to SFO, which makes courier service the far more practical option for North Las Vegas travelers.",
    },
    {
      q: "Can I get an emergency passport at McCarran International Airport (LAS)?",
      a: "No. Despite LAS being one of the busiest international airports in the country, there is no passport agency or emergency processing facility at the airport. This surprises many North Las Vegas travelers given LAS's size and international route network. All emergency processing requires routing through the San Francisco Passport Agency.",
    },
    {
      q: "Does Nellis AFB process passports for military families?",
      a: "Nellis AFB has a military post office that can accept DS-11 applications for active-duty personnel and dependents, but it does not process or expedite passports. It functions as a standard acceptance facility only. Military families on short PCS timelines should use a registered courier service for the fastest processing — especially when orders give less than 4 weeks before departure.",
    },
    {
      q: "I live in Aliante — where is the closest passport acceptance facility?",
      a: "The Aliante Post Office at 7051 N Aliante Parkway accepts passport applications as a USPS passport acceptance facility. For more appointment availability, the Clark County Government Center at 500 S Grand Central Parkway downtown has the largest number of daily appointment slots. Call (702) 455-0000 for County Clerk passport services.",
    },
    {
      q: "How fast can I get a passport in North Las Vegas?",
      a: "Fast Passport Center's emergency service processes passports in 24–48 hours for North Las Vegas residents via courier to San Francisco. Standard expedited service runs 2–9 business days. LAS airport's overnight cargo infrastructure makes courier routing to SFO consistently reliable year-round.",
    },
    {
      q: "Can I use a passport card instead of a passport book for a flight from LAS to Mexico?",
      a: "No. Passport cards are only valid for land and sea border crossings — they cannot be used for international air travel under any circumstances. If you fly from LAS to Mexico, Canada, the Caribbean, or anywhere international, you must present a valid U.S. passport book. Many North Las Vegas residents with passport cards from frequent Mexico land crossings learn this at the gate.",
    },
    {
      q: "Where can I get passport photos in North Las Vegas?",
      a: "Walgreens at 6395 N Decatur Blvd (at the I-215/Decatur intersection) and CVS at 6440 N Decatur Blvd offer passport photo services. For Aliante residents, the Walgreens at 6855 N Aliante Pkwy near the Aliante Casino is most convenient. All must meet 2x2 inch specifications with a pure white background and no glasses.",
    },
    {
      q: "What documents do I need for a child passport in Clark County?",
      a: "Both parents (or guardians) must appear in person with the child at a passport acceptance facility for DS-11 applications. Bring the child's certified birth certificate, both parents' government IDs, and photocopies of both IDs. If one parent cannot appear, a notarized DS-3053 Statement of Consent form is required. This is particularly important for Nellis AFB families when one parent is already deployed.",
    },
    {
      q: "I work at the Las Vegas Motor Speedway and need a passport fast for an international event — can you help?",
      a: "Absolutely. Speedway event professionals, logistics coordinators, and racing teams in North Las Vegas regularly use Fast Passport Center for rush processing before international events. Our 2–9 business day courier service handles document review, photo verification, and secure submission — ideal when a trade show or overseas event pops up on short notice.",
    },
    {
      q: "Does the Clark County Clerk accept walk-in passport applications?",
      a: "Passport acceptance at the Clark County Government Center is by appointment only — walk-ins are not accepted. Appointment availability can range from same-week to 2–3 weeks out depending on season. Call (702) 455-0000 or book online through the Clark County website. Post office acceptance facilities also require appointments.",
    },
    {
      q: "Is overnight courier reliable from North Las Vegas to San Francisco?",
      a: "Yes. Overnight courier from Las Vegas to San Francisco is one of the most reliable routes in the western United States. LAS airport handles significant overnight cargo volume, and the LV-to-SF corridor has multiple daily flights. Fast Passport Center has never had a weather-related delay on this route — unlike the mountain-west routes from smaller towns.",
    },
    {
      q: "I'm a real estate agent in the Cheyenne Corridor — can you help with a last-minute passport for an international property expo?",
      a: "Yes. North Las Vegas real estate professionals attending international expos, investment conferences, or overseas property tours frequently use our rush service. The 2–9 business day courier turnaround is ideal when a last-minute international opportunity arises. Complete the online application and ship documents overnight — we handle the rest.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Decatur & 215", address: "6395 N Decatur Blvd, North Las Vegas, NV 89085", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Decatur", address: "6440 N Decatur Blvd, North Las Vegas, NV 89084", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walgreens — Aliante", address: "6855 N Aliante Pkwy, North Las Vegas, NV 89084", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Lake Mead", address: "2060 E Lake Mead Blvd, North Las Vegas, NV 89030", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Centennial", address: "7951 W Tropical Pkwy, Las Vegas, NV 89149", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–2PM" },
    { name: "FedEx Office — Las Vegas Strip", address: "3765 S Las Vegas Blvd, Las Vegas, NV 89109", hours: "Mon–Fri: 7:30AM–9PM, Sat–Sun: 9AM–6PM" },
  ],
  dmvLocations: [
    { name: "NV DMV — North Decatur", address: "4110 N Decatur Blvd, Las Vegas, NV 89130", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Closest full-service DMV to North Las Vegas. REAL ID, driver's license, and state ID services for northern metro residents.", featured: true },
    { name: "NV DMV — Flamingo", address: "8250 W Flamingo Rd, Las Vegas, NV 89147", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Largest DMV in the Las Vegas Valley. More appointment availability. About 20 miles southwest of North Las Vegas.", featured: false },
    { name: "NV DMV — Henderson", address: "1399 American Pacific Dr, Henderson, NV 89074", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Southeastern valley DMV option. About 25 miles from North Las Vegas — good for those near the I-515 corridor.", featured: false },
  ],
  airports: [
    {
      code: "LAS",
      name: "Harry Reid International Airport",
      distance: "12 miles south of North Las Vegas",
      address: "5757 Wayne Newton Blvd, Las Vegas, NV 89119",
      note: "The primary international gateway for all of Southern Nevada. Direct flights to Europe, Asia, and the Americas. About 20 minutes from North Las Vegas via I-15. Major hub for Southwest, Spirit, and Frontier with extensive international service.",
      flightUrl: "https://www.harryreidairport.com/",
      flightLabel: "LAS Airport",
      featured: true,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "565 miles northwest — co-located with SF Passport Agency",
      address: "San Francisco, CA 94128",
      note: "Co-located with the closest passport agency. North Las Vegas travelers needing emergency same-day processing would need a round-trip flight to SFO — frequent daily flights from LAS.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Airport",
      featured: false,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "270 miles southwest of North Las Vegas",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Second closest major international gateway. About 4–5 hours driving or a 1-hour flight from LAS. Home to the Los Angeles Passport Agency — another emergency processing option via courier.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Airport",
      featured: false,
    },
    {
      code: "VGT",
      name: "North Las Vegas Airport",
      distance: "3 miles southwest — North Las Vegas",
      address: "2730 Airport Dr, North Las Vegas, NV 89032",
      note: "General aviation airport in North Las Vegas. No commercial or international service, but one of the busiest general aviation airports in the country. Not relevant for passport or international travel.",
      flightUrl: "https://www.vgt.aero/",
      flightLabel: "VGT Airport",
      featured: false,
    },
  ],
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Birth & Death Certificates", desc: "Nevada Department of Health and Human Services Office of Vital Records in Carson City — order certified birth and death certificates for Clark County residents.", url: "https://dpbh.nv.gov/Programs/VitalRecords/" },
    { icon: "ri-heart-line", label: "Marriage Records", desc: "Clark County Clerk — certified marriage licenses for name change passport applications.", url: "https://www.clarkcountynv.gov/" },
    { icon: "ri-scales-line", label: "Divorce Decrees", desc: "Clark County District Court — certified divorce decrees supporting passport name change.", url: "https://www.clarkcountycourts.us/" },
    { icon: "ri-global-line", label: "VitalChek — Nationwide Records", desc: "Order certified copies online through VitalChek for delivery in 5–10 business days.", url: "https://www.vitalchek.com/" },
  ],
};